import React, { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";

// ============================================================================
// TYPES
// ============================================================================

interface DashboardUser {
  _id: string;
  email: string;
  name: string;
  role: "admin" | "member";
  permissions: string[];
  status: "active" | "pending";
  avatarColor: string;
  lastActiveAt?: string;
  createdAt: string;
}

interface Invite {
  _id: string;
  email: string;
  permissions: string[];
  invitedBy: string;
  createdAt: string;
}

// Available tabs/permissions - matches sidebar navigation (only active tabs)
const AVAILABLE_TABS = [
  // Overview
  { id: "dashboard", label: "Dashboard", icon: "dashboard", category: null },
  { id: "leaderboard", label: "Leaderboard", icon: "trophy", category: null },
  // Sales Analytics
  { id: "daily-report", label: "Daily Report", icon: "document", category: "Sales Analytics" },
  { id: "acquisition", label: "Acquisition", icon: "chart", category: "Sales Analytics" },
  { id: "closers", label: "Closers", icon: "users", category: "Sales Analytics" },
  { id: "setters", label: "Setters", icon: "users", category: "Sales Analytics" },
  { id: "payment-collection", label: "Payment Collection", icon: "briefcase", category: "Sales Analytics" },
  { id: "mrr", label: "MRR", icon: "chart", category: "Sales Analytics" },
  // Pipeline
  { id: "leads", label: "Leads", icon: "users", category: "Pipeline" },
  { id: "buying-cycle", label: "Buying Cycle", icon: "chart", category: "Pipeline" },
  // Tools
  { id: "utm-builder", label: "UTM Builder", icon: "cog", category: "Tools" },
  { id: "rev-projector", label: "Rev Projector", icon: "cog", category: "Tools" },
  // Admin
  { id: "admin", label: "Admin", icon: "cog", category: null },
] as const;

// ============================================================================
// ICONS
// ============================================================================

const Icons = {
  Users: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  Plus: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  ),
  X: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  Trash: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  ),
  Check: () => (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  ),
  Mail: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  Clock: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  Shield: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  // Tab icons
  Dashboard: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
    </svg>
  ),
  Trophy: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3h14M5 3v6.5a6.5 6.5 0 0013 0V3M5 3H3v3a3 3 0 003 3m13-6h2v3a3 3 0 01-3 3m-5 6v3m-3 0h6" />
    </svg>
  ),
  Chart: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  Briefcase: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  Globe: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  ),
  Megaphone: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
    </svg>
  ),
  Document: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  Cog: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
};

// Helper to render tab icon
function TabIcon({ icon, className }: { icon: string; className?: string }) {
  const iconMap: Record<string, () => React.ReactElement> = {
    dashboard: Icons.Dashboard,
    trophy: Icons.Trophy,
    chart: Icons.Chart,
    briefcase: Icons.Briefcase,
    globe: Icons.Globe,
    megaphone: Icons.Megaphone,
    users: Icons.Users,
    document: Icons.Document,
    cog: Icons.Cog,
  };
  const IconComponent = iconMap[icon];
  return IconComponent ? <span className={className}><IconComponent /></span> : null;
}

// ============================================================================
// COMPONENTS
// ============================================================================

function Avatar({ name, color, size = "md" }: { name: string; color: string; size?: "sm" | "md" | "lg" }) {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const sizeClasses = {
    sm: "w-8 h-8 text-xs",
    md: "w-10 h-10 text-sm",
    lg: "w-12 h-12 text-base",
  };

  return (
    <div
      className={`${sizeClasses[size]} rounded-full flex items-center justify-center font-semibold text-white shadow-lg`}
      style={{ backgroundColor: color }}
    >
      {initials}
    </div>
  );
}

function RoleBadge({ role }: { role: "admin" | "member" }) {
  const isAdmin = role === "admin";
  return (
    <span
      className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
        ${isAdmin
          ? "bg-gradient-to-r from-amber-500/10 to-orange-500/10 text-amber-600 dark:text-amber-400 border border-amber-200/50 dark:border-amber-500/30"
          : "bg-gradient-to-r from-slate-500/10 to-gray-500/10 text-slate-600 dark:text-slate-300 border border-slate-200/50 dark:border-slate-500/30"
        }
      `}
    >
      {isAdmin && <Icons.Shield />}
      {isAdmin ? "Admin" : "Member"}
    </span>
  );
}

function StatusBadge({ status }: { status: "active" | "pending" }) {
  const isPending = status === "pending";
  return (
    <span
      className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
        ${isPending
          ? "bg-gradient-to-r from-yellow-500/10 to-amber-500/10 text-yellow-600 dark:text-yellow-400 border border-yellow-200/50 dark:border-yellow-500/30"
          : "bg-gradient-to-r from-emerald-500/10 to-green-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-500/30"
        }
      `}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isPending ? "bg-yellow-500" : "bg-emerald-500"}`} />
      {isPending ? "Pending" : "Active"}
    </span>
  );
}

function PermissionToggle({
  enabled,
  onChange,
  disabled = false,
}: {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={`
        relative w-11 h-6 rounded-full transition-all duration-300 ease-out
        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        ${enabled
          ? "bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-gradient-end)] shadow-lg shadow-[var(--brand-primary)]/25"
          : "bg-gray-200 dark:bg-gray-600"
        }
      `}
    >
      <span
        className={`
          absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-md
          transition-transform duration-300 ease-out flex items-center justify-center
          ${enabled ? "translate-x-5" : "translate-x-0"}
        `}
      >
        {enabled && (
          <span className="text-[var(--brand-primary)] scale-75">
            <Icons.Check />
          </span>
        )}
      </span>
    </button>
  );
}

function UserRow({
  user,
  onUpdatePermissions,
  onDelete,
}: {
  user: DashboardUser;
  onUpdatePermissions: (userId: string, permissions: string[]) => void;
  onDelete: (userId: string) => void;
}) {
  const [isDeleting, setIsDeleting] = useState(false);
  const permissions = user.permissions || [];

  const handleTogglePermission = (tabId: string) => {
    const newPermissions = permissions.includes(tabId)
      ? permissions.filter((p) => p !== tabId)
      : [...permissions, tabId];
    onUpdatePermissions(user._id, newPermissions);
  };

  const handleDelete = async () => {
    if (confirm(`Remove ${user.name} from the dashboard?`)) {
      setIsDeleting(true);
      await onDelete(user._id);
    }
  };

  return (
    <tr className="group hover:bg-muted/50 transition-colors">
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <Avatar name={user.name || "?"} color={user.avatarColor} />
          <div>
            <p className="font-medium text-foreground">{user.name || "Unknown"}</p>
            <p className="text-sm text-muted-foreground">{user.email || ""}</p>
          </div>
        </div>
      </td>
      <td className="py-4 px-4">
        <RoleBadge role={user.role || "member"} />
      </td>
      <td className="py-4 px-4">
        <StatusBadge status={user.status || "pending"} />
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-4">
          {AVAILABLE_TABS.map((tab) => (
            <div key={tab.id} className="flex items-center gap-2">
              <PermissionToggle
                enabled={permissions.includes(tab.id)}
                onChange={() => handleTogglePermission(tab.id)}
                disabled={user.role === "admin" && tab.id === "admin"}
              />
              <span className="text-xs text-muted-foreground hidden xl:inline">{tab.label}</span>
            </div>
          ))}
        </div>
      </td>
      <td className="py-4 px-4 text-right">
        <button
          onClick={handleDelete}
          disabled={isDeleting}
          className="p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all opacity-0 group-hover:opacity-100"
        >
          <Icons.Trash />
        </button>
      </td>
    </tr>
  );
}

function InvitePanel({
  isOpen,
  onClose,
  onInvite,
}: {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (email: string, name: string, permissions: string[]) => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [permissions, setPermissions] = useState<string[]>(["website"]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await onInvite(email, name, permissions);
      setEmail("");
      setName("");
      setPermissions(["website"]);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send invite");
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePermission = (tabId: string) => {
    setPermissions((prev) =>
      prev.includes(tabId) ? prev.filter((p) => p !== tabId) : [...prev, tabId]
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed right-0 top-0 h-full w-full max-w-md bg-card shadow-2xl z-50 transform transition-transform duration-500 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-foreground">Invite Team Member</h3>
                <p className="text-sm text-muted-foreground mt-1">Add someone to your dashboard</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icons.X />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 p-6 overflow-y-auto">
            <div className="space-y-6">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary)]/20 outline-none transition-all"
                  required
                />
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@company.com"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary)]/20 outline-none transition-all"
                  required
                />
              </div>

              {/* Permissions */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-3">
                  Tab Access
                </label>
                <div className="space-y-4">
                  {/* Tabs without category */}
                  {AVAILABLE_TABS.filter(tab => !tab.category).map((tab) => (
                    <label
                      key={tab.id}
                      className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        permissions.includes(tab.id)
                          ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5"
                          : "border-border hover:border-muted-foreground/30"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                            permissions.includes(tab.id)
                              ? "bg-[var(--brand-primary)] text-white"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          <TabIcon icon={tab.icon} />
                        </div>
                        <span className="font-medium text-foreground text-sm">{tab.label}</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={permissions.includes(tab.id)}
                        onChange={() => togglePermission(tab.id)}
                        className="sr-only"
                      />
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                          permissions.includes(tab.id)
                            ? "border-[var(--brand-primary)] bg-[var(--brand-primary)] text-white"
                            : "border-muted-foreground/50"
                        }`}
                      >
                        {permissions.includes(tab.id) && <Icons.Check />}
                      </div>
                    </label>
                  ))}

                  {/* Sales Analytics Category */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">Sales Analytics</p>
                    {AVAILABLE_TABS.filter(tab => tab.category === "Sales Analytics").map((tab) => (
                      <label
                        key={tab.id}
                        className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${
                          permissions.includes(tab.id)
                            ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5"
                            : "border-border hover:border-muted-foreground/30"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                              permissions.includes(tab.id)
                                ? "bg-[var(--brand-primary)] text-white"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            <TabIcon icon={tab.icon} />
                          </div>
                          <span className="font-medium text-foreground text-sm">{tab.label}</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={permissions.includes(tab.id)}
                          onChange={() => togglePermission(tab.id)}
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                            permissions.includes(tab.id)
                              ? "border-[var(--brand-primary)] bg-[var(--brand-primary)] text-white"
                              : "border-muted-foreground/50"
                          }`}
                        >
                          {permissions.includes(tab.id) && <Icons.Check />}
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Pipeline Category */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">Pipeline</p>
                    {AVAILABLE_TABS.filter(tab => tab.category === "Pipeline").map((tab) => (
                      <label
                        key={tab.id}
                        className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${
                          permissions.includes(tab.id)
                            ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5"
                            : "border-border hover:border-muted-foreground/30"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                              permissions.includes(tab.id)
                                ? "bg-[var(--brand-primary)] text-white"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            <TabIcon icon={tab.icon} />
                          </div>
                          <span className="font-medium text-foreground text-sm">{tab.label}</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={permissions.includes(tab.id)}
                          onChange={() => togglePermission(tab.id)}
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                            permissions.includes(tab.id)
                              ? "border-[var(--brand-primary)] bg-[var(--brand-primary)] text-white"
                              : "border-muted-foreground/50"
                          }`}
                        >
                          {permissions.includes(tab.id) && <Icons.Check />}
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Tools Category */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">Tools</p>
                    {AVAILABLE_TABS.filter(tab => tab.category === "Tools").map((tab) => (
                      <label
                        key={tab.id}
                        className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${
                          permissions.includes(tab.id)
                            ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5"
                            : "border-border hover:border-muted-foreground/30"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                              permissions.includes(tab.id)
                                ? "bg-[var(--brand-primary)] text-white"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            <TabIcon icon={tab.icon} />
                          </div>
                          <span className="font-medium text-foreground text-sm">{tab.label}</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={permissions.includes(tab.id)}
                          onChange={() => togglePermission(tab.id)}
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                            permissions.includes(tab.id)
                              ? "border-[var(--brand-primary)] bg-[var(--brand-primary)] text-white"
                              : "border-muted-foreground/50"
                          }`}
                        >
                          {permissions.includes(tab.id) && <Icons.Check />}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {error && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}
            </div>
          </form>

          {/* Footer */}
          <div className="p-6 border-t border-border bg-muted/30">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 rounded-xl border border-border text-foreground font-medium hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !email || !name}
                className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-gradient-end)] text-white font-medium hover:shadow-lg hover:shadow-[var(--brand-primary)]/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Icons.Mail />
                    Send Invite
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function PendingInvitesCard({ invites, onCancel }: { invites: Invite[]; onCancel: (id: string) => void }) {
  if (invites.length === 0) return null;

  return (
    <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 dark:from-amber-500/5 dark:to-orange-500/5 rounded-2xl p-6 border border-amber-200/50 dark:border-amber-500/20 opacity-0 animate-fade-in-up stagger-3">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-amber-500/20 text-amber-600 dark:text-amber-400">
          <Icons.Clock />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">Pending Invitations</h3>
          <p className="text-sm text-muted-foreground">{invites.length} invite{invites.length !== 1 ? "s" : ""} awaiting response</p>
        </div>
      </div>

      <div className="space-y-3">
        {invites.map((invite) => (
          <div
            key={invite._id}
            className="flex items-center justify-between p-3 bg-card rounded-xl border border-amber-200/50 dark:border-amber-500/20"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-600 dark:text-amber-400">
                <Icons.Mail />
              </div>
              <div>
                <p className="font-medium text-foreground text-sm">{invite.email}</p>
                <p className="text-xs text-muted-foreground">
                  Invited {format(new Date(invite.createdAt), "MMM d, yyyy")}
                </p>
              </div>
            </div>
            <button
              onClick={() => onCancel(invite._id)}
              className="text-xs text-muted-foreground hover:text-red-500 px-3 py-1.5 hover:bg-red-500/10 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function Settings() {
  const [users, setUsers] = useState<DashboardUser[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [invitePanelOpen, setInvitePanelOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, invitesRes] = await Promise.all([
        fetch("/api/users"),
        fetch("/api/invites"),
      ]);

      const usersData = await usersRes.json();
      const invitesData = await invitesRes.json();

      if (usersData.success) setUsers(usersData.data);
      if (invitesData.success) setInvites(invitesData.data);
    } catch (error) {
      console.error("Error fetching settings data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInvite = async (email: string, name: string, permissions: string[]) => {
    const response = await fetch("/api/users/invite", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, name, permissions }),
    });

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error);
    }

    await fetchData();
  };

  const handleUpdatePermissions = async (userId: string, permissions: string[]) => {
    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ permissions }),
      });

      const result = await response.json();
      if (result.success) {
        setUsers((prev) =>
          prev.map((u) => (u._id === userId ? { ...u, permissions } : u))
        );
      }
    } catch (error) {
      console.error("Error updating permissions:", error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      const response = await fetch(`/api/users/${userId}`, { method: "DELETE" });
      const result = await response.json();
      if (result.success) {
        setUsers((prev) => prev.filter((u) => u._id !== userId));
      }
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    try {
      const response = await fetch(`/api/invites/${inviteId}`, { method: "DELETE" });
      const result = await response.json();
      if (result.success) {
        await fetchData();
      }
    } catch (error) {
      console.error("Error cancelling invite:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-4 border-[var(--brand-primary)]/20 border-t-[var(--brand-primary)] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between opacity-0 animate-fade-in-up">
        <div>
          <h2 className="text-2xl font-bold mb-2">Team Settings</h2>
          <p className="text-muted-foreground">Manage who has access to your dashboard</p>
        </div>
        <button
          onClick={() => setInvitePanelOpen(true)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-gradient-end)] text-white font-medium hover:shadow-lg hover:shadow-[var(--brand-primary)]/25 transition-all"
        >
          <Icons.Plus />
          Invite Member
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 opacity-0 animate-fade-in-up stagger-1">
        <div className="bg-card rounded-2xl p-6 shadow-brand">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-[var(--brand-primary)]/10 to-[var(--brand-gradient-end)]/10">
              <div className="text-[var(--brand-primary)]">
                <Icons.Users />
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Team Members</p>
              <p className="text-2xl font-bold">{users.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-2xl p-6 shadow-brand">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500/10 to-green-500/10">
              <div className="text-emerald-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active</p>
              <p className="text-2xl font-bold">{users.filter((u) => u.status === "active").length}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-2xl p-6 shadow-brand">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500/10 to-orange-500/10">
              <div className="text-amber-500">
                <Icons.Clock />
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Pending Invites</p>
              <p className="text-2xl font-bold">{invites.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Invites */}
      <PendingInvitesCard invites={invites} onCancel={handleCancelInvite} />

      {/* Users Table */}
      <div className="bg-card rounded-2xl shadow-brand overflow-hidden opacity-0 animate-fade-in-up stagger-2">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold">Team Members</h3>
          <p className="text-sm text-muted-foreground">Manage permissions for each team member</p>
        </div>

        {users.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
              <Icons.Users />
            </div>
            <p className="text-muted-foreground mb-4">No team members yet</p>
            <button
              onClick={() => setInvitePanelOpen(true)}
              className="text-[var(--brand-primary)] font-medium hover:underline"
            >
              Invite your first member
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">User</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Role</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Permissions</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <UserRow
                    key={user._id}
                    user={user}
                    onUpdatePermissions={handleUpdatePermissions}
                    onDelete={handleDeleteUser}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite Panel */}
      <InvitePanel
        isOpen={invitePanelOpen}
        onClose={() => setInvitePanelOpen(false)}
        onInvite={handleInvite}
      />
    </div>
  );
}
